import React, {useState} from 'react';
import {Box, Typography, Grid, Tabs, Tab, Tooltip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import NationalDashboardShell from '../components/NationalDashboard.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import { useDashboard } from '../../../context/DashboardContext';
import {ParticipationBuildupChart} from '../../DashboardCommon/ParticipationBuildupChart.comp';
import { PARTICIPATION_BAND_COLORS } from '../../../utils/constants';

import {BandChip as SharedBandChip} from '../../DashboardCommon/widgets.comp';
import {UNIT, fmtValue} from '../../DashboardCommon/units';
import {useTableControls} from '../../DashboardCommon/useTableControls';
import {SortFilterHeaderCell} from '../../DashboardCommon/SortFilterHeaderCell.comp';
import {TableToolbar} from '../../DashboardCommon/TableToolbar.comp';
const NAVY   = '#052F44';
const ORANGE = '#FF6900';

/* ════════════════════════════════════════════════════════════════ */
/*  TAB 1 — OVERVIEW DATA                                          */
/* ════════════════════════════════════════════════════════════════ */

const KPI_CARDS = [
    {label: 'CHILDREN PARTICIPATED', value: '1.24M', accent: NAVY},
    {label: 'SCHOOLS PARTICIPATED',  value: '8,420', accent: '#1A6A7A'},
    {label: 'COVERAGE RATE',         value: '78%',   accent: '#2A8080'},
    {label: 'DATA CONFIDENCE',       value: 'High',  accent: '#1B6B55'},
];

const STATUS_STYLE = {
    High:  {bg: 'var(--brand-surface-subtle, #D1FAE5)', color: '#065F46'},
    Watch: {bg: 'var(--brand-surface-subtle, #FEF3C7)', color: '#92400E'},
    OK:    {bg: 'var(--brand-surface-subtle, #DCFCE7)', color: '#15803D'},
};

const COVERAGE_ROWS = [
    {area: 'National',        rate: '84%', status: 'High'},
    {area: 'Region coverage', rate: '76%', status: 'Watch'},
    {area: 'School type',     rate: '72%', status: 'Watch'},
    {area: 'Demographics',    rate: '80%', status: 'OK'},
];

const OVERVIEW_GUIDANCE = [
    'If participation is uneven by region or school type, interpret comparisons cautiously.',
    'Before acting on weak scores, check whether the sample is representative.',
    'Under-participating areas should be followed up before final policy conclusions.',
];

/* ════════════════════════════════════════════════════════════════ */
/*  TAB 2 — BY REGION DATA                                         */
/* ════════════════════════════════════════════════════════════════ */

const THRESHOLD = 75;

const REGIONAL_BARS = [
    {region: 'London',     pct: 91},
    {region: 'Scotland',   pct: 86},
    {region: 'Midlands',   pct: 82},
    {region: 'Wales',      pct: 77},
    {region: 'North East', pct: 68},
    {region: 'N. Ireland', pct: 61},
];

const ACTION_STYLE = {
    'Follow up': {bg: 'var(--brand-surface-subtle, #FEE2E2)', color: '#C53030'},
    'OK':        {bg: 'var(--brand-surface-subtle, #DCFCE7)', color: '#15803D'},
};

const UNDERREP_ROWS = [
    {region: 'North East', rate: '68%', action: 'Follow up'},
    {region: 'N. Ireland', rate: '61%', action: 'Follow up'},
    {region: 'Wales',      rate: '77%', action: 'OK'},
    {region: 'Midlands',   rate: '82%', action: 'OK'},
];

const RECOMMENDED_ACTIONS = [
    'Contact regional leads below 75% participation before finalizing regional comparisons.',
    'Flag low-participation regions in heatmaps and trend charts.',
    'If response volume remains low, aggregate regions or suppress comparisons.',
];

/* ════════════════════════════════════════════════════════════════ */
/*  SHARED SUB-COMPONENTS                                           */
/* ════════════════════════════════════════════════════════════════ */

/* Solid band pill (matches School Dashboard's BandChip) */
const participationBandColor = (band) => PARTICIPATION_BAND_COLORS[band] || '#6B7280';

const BandChip = ({label}) => <SharedBandChip label={label} color={participationBandColor(label)}/>;

const KpiCard = ({label, value, accent}) => {
    // Word-based values (e.g. "Very Low") read oversized at the same 36px
    // used for short numeric values (e.g. "14", "6%") — scale them down so
    // every card's headline sits at a consistent visual weight.
    const isWordValue = typeof value === 'string' && /[a-zA-Z]/.test(value);

    return (
        <Box sx={{
            flex: 1,
            border: '1px solid var(--brand-border, #E5E7EB)',
            borderRadius: '10px',
            backgroundColor: 'var(--brand-surface, #ffffff)',
            boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
        }}>
            <Box sx={{height: '5px', backgroundColor: accent, flexShrink: 0}}/>
            <Box sx={{p: 2.5, flex: 1}}>
                <Typography sx={{fontSize: '11px', fontWeight: 700, color: '#6A7282', letterSpacing: '0.7px', textTransform: 'uppercase', mb: 1.2}}>
                    {label}
                </Typography>
                <Typography sx={{fontSize: isWordValue ? '22px' : '36px', fontWeight: 800, color: NAVY, lineHeight: 1.2, mb: 0.5}}>
                    {value}
                </Typography>
            </Box>
        </Box>
    );
};

const StatusPill = ({status}) => {
    const {bg, color} = STATUS_STYLE[status] || {bg: 'var(--brand-surface-muted, #F3F4F6)', color: '#374151'};
    return (
        <Box sx={{display: 'inline-block', backgroundColor: bg, color, fontSize: '12px', fontWeight: 700, px: 1.5, py: 0.4, borderRadius: '6px', textAlign: 'center', minWidth: '54px'}}>
            {status}
        </Box>
    );
};

/* ════════════════════════════════════════════════════════════════ */
/*  TAB 1 — OVERVIEW                                               */
/* ════════════════════════════════════════════════════════════════ */

const OverviewTab = ({ s9Section, loading }) => {
    if (loading) {
        return <CircularLoader minHeight="300px" />;
    }
    if (!s9Section) {
        return <NoDataAvailable />;
    }

    const kpiCardsFromData = s9Section?.cards?.filter(c => c.card_type === 'KPICard') || [];
    
    // Map them to the format expected by KpiCard:
    const mappedKpis = kpiCardsFromData.map((c, idx) => {
        let label = c.kpi_name;
        if (label === 'schools_count') {
            label = 'SCHOOLS PARTICIPATED';
        } else {
            label = label.toUpperCase();
        }
        
        let value = c.kpi_value;
        if (label === 'COVERAGE RATE' && typeof value === 'number') {
            value = `${value}%`;
        }
        
        // Color accent matching the original sequence
        const accents = [NAVY, '#1A6A7A', '#2A8080', '#1B6B55'];
        const accent = accents[idx % accents.length];

        return { label, value, accent };
    });

    // Add "DATA CONFIDENCE" card if we only got 3 KPI cards but have a band
    if (mappedKpis.length === 3) {
        const coverageRateCard = kpiCardsFromData.find(c => c.kpi_name === 'COVERAGE RATE');
        const confidenceValue = coverageRateCard?.kpi_band || 'High';
        mappedKpis.push({
            label: 'DATA CONFIDENCE',
            value: confidenceValue,
            accent: '#1B6B55'
        });
    }

    const trendCard = s9Section?.cards?.find(c => c.card_type === 'TrendLineCard');
    const leftLineCard = s9Section?.cards?.find(c => c.card_type === 'LeftLineCard');

    // Update COVERAGE_ROWS rate dynamically if available
    const coverageRateVal = kpiCardsFromData.find(c => c.kpi_name === 'COVERAGE RATE')?.kpi_value;
    const dynamicCoverageRows = COVERAGE_ROWS.map(row => {
        if (row.area === 'National' && coverageRateVal != null) {
            return {
                ...row,
                rate: `${coverageRateVal}%`
            };
        }
        return row;
    });

    return (
        <Box>
            {/* KPI cards */}
            <Box sx={{display: 'flex', gap: 2, mb: 3}}>
                {mappedKpis.map(c => <KpiCard key={c.label} {...c}/>)}
            </Box>

            {/* Bottom row */}
            <Grid container spacing={2.5} alignItems="stretch" sx={{mb: 3}}>
                {/* Coverage build-up chart */}
                <Grid item xs={12} md={12} sx={{display: 'flex', flexDirection: 'column'}}>
                    <Box sx={{border: '2px solid #7DBFD8', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, flex: 1}}>
                        <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 2.5}}>
                            {trendCard?.title || "Coverage build-up by month"}
                        </Typography>
                        {/* Bars = learners added in the month, line = running total (QA 9 Oct: a single month must still show). */}
                        <ParticipationBuildupChart card={trendCard} bare height={200}/>
                    </Box>
                </Grid>

                {/* Coverage status table - Hidden for now
                <Grid item xs={12} md={5} sx={{display: 'flex', flexDirection: 'column'}}>
                    <Box sx={{border: '2px solid #7DBFD8', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden', flex: 1}}>
                        <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY, p: 2.5, pb: 1.5}}>
                            Coverage status
                        </Typography>
                        <Box sx={{display: 'grid', gridTemplateColumns: '1fr 72px 80px', backgroundColor: NAVY, px: 2.5, py: 1.2}}>
                            <Typography sx={{fontSize: '12px', fontWeight: 700, color: '#ffffff'}}>Area</Typography>
                            <Typography sx={{fontSize: '12px', fontWeight: 700, color: '#ffffff', textAlign: 'center'}}>Rate</Typography>
                            <Typography sx={{fontSize: '12px', fontWeight: 700, color: '#ffffff', textAlign: 'center'}}>Status</Typography>
                        </Box>
                        {dynamicCoverageRows.map((row, i) => (
                            <Box key={row.area} sx={{display: 'grid', gridTemplateColumns: '1fr 72px 80px', alignItems: 'center', px: 2.5, py: 1.4, backgroundColor: i % 2 === 0 ? 'var(--brand-surface, #ffffff)' : 'var(--brand-surface-subtle, #F9FAFB)', borderBottom: i < dynamicCoverageRows.length - 1 ? '1px solid #F3F4F6' : 'none'}}>
                                <Typography sx={{fontSize: '13px', color: '#374151'}}>{row.area}</Typography>
                                <Typography sx={{fontSize: '13px', fontWeight: 600, color: NAVY, textAlign: 'center'}}>{row.rate}</Typography>
                                <Box sx={{display: 'flex', justifyContent: 'center'}}>
                                    <StatusPill status={row.status}/>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Grid>
                */}
            </Grid>

            {/* Decision guidance */}
            {leftLineCard?.leftline_html ? (
                <Box 
                    sx={{
                        border: '1px solid #C7D9E8', 
                        borderLeft: `4px solid ${NAVY}`, 
                        borderRadius: '8px', 
                        backgroundColor: 'var(--brand-surface-subtle, #F0F6FB)', 
                        p: 2.5,
                        '& p': { m: 0, fontSize: '13px', color: '#374151', lineHeight: 1.6, mb: 1 },
                        '& p:last-child': { mb: 0 },
                        '& strong': { color: NAVY }
                    }}
                    dangerouslySetInnerHTML={{ __html: leftLineCard.leftline_html }}
                />
            ) : (
                <Box sx={{border: '1px solid #C7D9E8', borderLeft: `4px solid ${NAVY}`, borderRadius: '8px', backgroundColor: 'var(--brand-surface-subtle, #F0F6FB)', p: 2.5}}>
                    <Typography sx={{fontSize: '14px', fontWeight: 700, color: NAVY, mb: 1}}>Decision guidance</Typography>
                    {OVERVIEW_GUIDANCE.map((text, i) => (
                        <Box key={i} sx={{display: 'flex', gap: 1, mb: i < OVERVIEW_GUIDANCE.length - 1 ? 0.8 : 0}}>
                            <Typography sx={{fontSize: '13px', color: NAVY, flexShrink: 0, mt: 0.05}}>•</Typography>
                            <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.6}}>{text}</Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

/* ════════════════════════════════════════════════════════════════ */
/*  TAB 2 — BY REGION                                              */
/* ════════════════════════════════════════════════════════════════ */

/* Matches School Dashboard's ParticipationByGrade CompletionBar exactly:
   label + pct + band pill on top, then a pill-shaped progress track with
   a threshold marker. */
const CompletionBar = ({label, pct, band, threshold}) => (
    <Box sx={{mb: 3}}>
        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75}}>
            <Typography sx={{fontSize: '14px', fontWeight: 700, color: NAVY}}>{label}</Typography>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <Typography sx={{fontSize: '15px', fontWeight: 800, color: NAVY}}>{pct}%</Typography>
                {band && <BandChip label={band}/>}
            </Box>
        </Box>
        <Box sx={{position: 'relative', height: 24, borderRadius: '12px', backgroundColor: 'var(--brand-surface-muted, #F3F4F6)'}}>
            <Tooltip title={`${pct}%`} arrow placement="top">
                <Box sx={{position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, height: '100%', backgroundColor: participationBandColor(band), borderRadius: '12px', overflow: 'hidden', cursor: 'pointer'}}/>
            </Tooltip>
            <Tooltip title={`Threshold: ${threshold}%`} arrow placement="top">
                <Box sx={{
                    position: 'absolute', left: `${threshold}%`, top: '-9px', width: 0, height: 0,
                    borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
                    borderTop: '9px solid #374151',
                    transform: 'translateX(-50%)', cursor: 'pointer', zIndex: 4,
                }}/>
            </Tooltip>
        </Box>
    </Box>
);

/* Region/Participants/Registered/Rate/Status table — built from the same
   BarChartBMCard items the completion bars use (item.data carries the raw
   participant counts), since this section has no separate TableCard. */
// A MUI table (was a plain <table>) so every column sorts and filters from its header (QA 9 Oct 2026).
const regionHeadCell = {backgroundColor: NAVY, color: '#ffffff', fontSize: '12px', fontWeight: 700, borderBottom: 'none', py: 1.2, px: 2};
const regionBodyCell = {py: 1.4, px: 2, borderBottom: '1px solid #F3F4F6', color: '#374151', fontSize: '13px'};
const REGION_COLUMNS = [
    {key: 'label', label: 'Region', type: 'text'},
    {key: 'participants', label: 'Participants', type: 'number'},
    {key: 'registered', label: 'Registered', type: 'number'},
    {key: 'rate', label: 'Rate %', type: 'percent'},
];

const RegionTable = ({title, rows}) => {
    const t = useTableControls(rows, REGION_COLUMNS);
    return (
        <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%'}}>
            <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY, mb: 2}}>
                {title}
            </Typography>
            {(t.chips.length > 0 || rows.length >= 5) && <TableToolbar {...t}/>}
            <TableContainer sx={{overflowX: 'auto'}}>
                <Table sx={{width: '100%'}}>
                    <TableHead>
                        <TableRow>
                            {REGION_COLUMNS.map((c, idx) => (
                                <SortFilterHeaderCell key={c.key} {...t.headerProps(c.key)} sx={{...regionHeadCell, textAlign: idx === 0 ? 'left' : 'center'}}/>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length > 0 && t.rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} sx={{...regionBodyCell, textAlign: 'center', color: '#6A7282', py: 2.5}}>No region matches the filters.</TableCell>
                            </TableRow>
                        )}
                        {t.rows.map((row, rowIndex) => (
                            <TableRow key={row.label} sx={{backgroundColor: rowIndex % 2 === 0 ? 'var(--brand-surface, #ffffff)' : 'var(--brand-surface-subtle, #F9FAFB)'}}>
                                <TableCell sx={{...regionBodyCell, fontWeight: 700, color: NAVY}}>
                                    {row.label}
                                </TableCell>
                                <TableCell sx={{...regionBodyCell, textAlign: 'center'}}>
                                    {row.participants.toLocaleString()}
                                </TableCell>
                                <TableCell sx={{...regionBodyCell, textAlign: 'center'}}>
                                    {row.registered.toLocaleString()}
                                </TableCell>
                                <TableCell sx={{...regionBodyCell, textAlign: 'center'}}>
                                    <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5}}>
                                        <Typography sx={{fontSize: '13px', fontWeight: 700, color: NAVY}}>{row.rate}</Typography>
                                        {row.band && <BandChip label={row.band}/>}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

// Dummy-data fix: the backend's catch-all region bucket comes through as
// "Other" (singular) — pluralize it for display in this Region-level view.
const regionLabel = (label) => (label?.trim() === 'Other' ? 'Others' : label);

const ByRegionTab = ({ s9aSection, loading }) => {
    if (loading) {
        return <CircularLoader minHeight="300px" />;
    }
    if (!s9aSection) {
        return <NoDataAvailable />;
    }

    const barCard = s9aSection?.cards?.find(c => c.card_type === 'BarChartBMCard');
    const leftLineCard = s9aSection?.cards?.find(c => c.card_type === 'LeftLineCard');

    const threshold = barCard?.items?.[0]?.benchmark_values?.find(b => b.name === 'Threshold')?.value
        ?? barCard?.items?.[0]?.benchmark_values?.[0]?.value
        ?? 75;

    const dynamicRegionalBars = barCard?.items?.map(item => ({
        label: regionLabel(item.label),
        pct: item.value !== undefined && item.value !== null ? Math.round(item.value) : 0,
        band: item.band || item.data?.band
    })) || [];

    // Region/Participants/Registered/Rate table — this section has no
    // separate TableCard, so rows come from the same BarChartBMCard items
    // (item.data carries the raw participant counts).
    const tableRows = barCard?.items?.map(item => {
        const rateVal = item.data?.rate ?? item.value ?? 0;
        return {
            label: regionLabel(item.label),
            participants: item.data?.participants ?? 0,
            registered: item.data?.total ?? 0,
            rate: fmtValue(rateVal, UNIT.PERCENT),
            band: item.band || item.data?.band
        };
    }) || [];

    return (
        <>
            <Grid container spacing={2.5} alignItems="stretch">
                {/* Left — completion bars */}
                <Grid item xs={12} md={7}>
                    <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%'}}>
                        <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY, mb: 3}}>
                            {barCard?.title || "Completion Rate by Region"} · {threshold}% threshold
                        </Typography>
                        {dynamicRegionalBars.map((r, idx) => (
                            <CompletionBar key={r.label || idx} {...r} threshold={threshold}/>
                        ))}
                    </Box>
                </Grid>

                {/* Right — under-represented regions table */}
                <Grid item xs={12} md={5}>
                    <RegionTable title={barCard?.title} rows={tableRows}/>
                </Grid>
            </Grid>

            {/* Recommended action */}
            <Box sx={{
                mt: 3, p: {xs: 2, md: 2.5}, backgroundColor: 'var(--brand-surface-subtle, #F0F7F4)', border: '1px solid #C6E0D8',
                borderLeft: `4px solid #1B6B55`, borderRadius: '0 8px 8px 0',
                '& p': { m: 0, fontSize: '13px', color: '#374151', lineHeight: 1.7, mb: 1 },
                '& p:last-child': { mb: 0 },
                '& strong': { color: '#1B6B55' }
            }}>
                {leftLineCard?.leftline_html ? (
                    <Box dangerouslySetInnerHTML={{ __html: leftLineCard.leftline_html }}/>
                ) : (
                    <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.7}}>
                        <strong style={{color: '#1B6B55'}}>Recommended action:&nbsp;</strong>
                        Contact regional leads below {threshold}% participation before finalizing regional comparisons.
                    </Typography>
                )}
            </Box>
        </>
    );
};

/* ════════════════════════════════════════════════════════════════ */
/*  MAIN COMPONENT                                                  */
/* ════════════════════════════════════════════════════════════════ */

const TAB_TITLES = [
    'Participation Overview',
    'Participation by Region',
];

const TAB_SUBTITLES = [
    'Is national assessment coverage representative enough to guide policy?',
    'Which regions have not participated enough for us to trust regional comparisons?',
];

const PARTICIPATION_OVERVIEW_SECTION_CODE = 'S_9';
const PARTICIPATION_BY_REGION_SECTION_CODE = 'S_9a';

const CARD_SECTIONS = [
    PARTICIPATION_OVERVIEW_SECTION_CODE,
    PARTICIPATION_BY_REGION_SECTION_CODE,
];

const ParticipationSection = () => {
    const [activeTab, setActiveTab] = useState(0);
    const { dashboardData, loading } = useDashboard({ card_sections: CARD_SECTIONS });

    // Defensively locate section S_9
    let s9Section = null;
    if (dashboardData) {
        if (dashboardData.section_code === PARTICIPATION_OVERVIEW_SECTION_CODE) {
            s9Section = dashboardData;
        } else if (dashboardData.cards?.sections) {
            s9Section = dashboardData.cards.sections.find(s => s.section_code === PARTICIPATION_OVERVIEW_SECTION_CODE);
        } else if (Array.isArray(dashboardData.sections)) {
            s9Section = dashboardData.sections.find(s => s.section_code === PARTICIPATION_OVERVIEW_SECTION_CODE);
        } else if (dashboardData.cards && Array.isArray(dashboardData.cards)) {
            s9Section = dashboardData;
        }
    }

    // Defensively locate section S_9a
    let s9aSection = null;
    if (dashboardData) {
        if (dashboardData.section_code === PARTICIPATION_BY_REGION_SECTION_CODE) {
            s9aSection = dashboardData;
        } else if (dashboardData.cards?.sections) {
            s9aSection = dashboardData.cards.sections.find(s => s.section_code === PARTICIPATION_BY_REGION_SECTION_CODE);
        } else if (Array.isArray(dashboardData.sections)) {
            s9aSection = dashboardData.sections.find(s => s.section_code === PARTICIPATION_BY_REGION_SECTION_CODE);
        } else if (dashboardData.cards && Array.isArray(dashboardData.cards)) {
            s9aSection = dashboardData;
        }
    }

    const titleCardS9 = s9Section?.cards?.find(c => c.card_type === 'TitleCard');
    const titleCardS9a = s9aSection?.cards?.find(c => c.card_type === 'TitleCard');

    const displayTitle = activeTab === 0 
        ? (titleCardS9?.title ? titleCardS9.title.trim() : TAB_TITLES[0])
        : (titleCardS9a?.title ? titleCardS9a.title.trim() : TAB_TITLES[1]);
        
    const displaySubtitle = activeTab === 0 
        ? (titleCardS9?.footer ? titleCardS9.footer : TAB_SUBTITLES[0])
        : (titleCardS9a?.footer ? titleCardS9a.footer : TAB_SUBTITLES[1]);

    return (
        <NationalDashboardShell
            tabs={
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    TabIndicatorProps={{style: {backgroundColor: ORANGE}}}
                    sx={{
                        borderBottom: '1px solid #E5E7EB',
                        mb: 1,
                        '& .MuiTab-root': {textTransform: 'none', fontWeight: 600, fontSize: '14px', color: '#6A7282', px: 2},
                        '& .Mui-selected': {color: `${ORANGE} !important`},
                    }}
                >
                    <Tab label="Overview"/>
                    <Tab label="By Region"/>
                </Tabs>
            }
        >
            {/* Page header */}
            <Box sx={{mt: 1.5, mb: 0.5}}>
                <Typography sx={{fontSize: '23px', fontWeight: 800, color: NAVY, lineHeight: 1.2}}>
                    {displayTitle}
                </Typography>
            </Box>

            <Typography sx={{fontSize: '15px', color: '#4B5563', fontStyle: 'italic', mb: 3}}>
                {displaySubtitle}
            </Typography>

            {activeTab === 0 && <OverviewTab s9Section={s9Section} loading={loading}/>}
            {activeTab === 1 && <ByRegionTab s9aSection={s9aSection} loading={loading}/>}
        </NationalDashboardShell>
    );
};

export default ParticipationSection;
